import { useQuery, useQueryClient } from '@tanstack/react-query'

import { RequestFieldsEnum } from '@/enums/RequestFieldsEnum'
import { useBaseRequest } from '@/hooks/useBaseRequest'
import { useCurrentCart } from '@/hooks/useCurrentCart'
import { useIsLoggedIn } from '@/hooks/useIsLoggedIn'
import { useOrgScopedQueryCacheKey } from '@/hooks/useOrgScopedQueryCacheKey'
import { appropriationsClient } from '@/lib/clients/appropriationsClient'

import type { GetUserAppropriationsRequest } from '@/gen/ecom/apis'

export function useAppropriations() {
  const { cartBaseRequest } = useBaseRequest()
  const isLoggedIn = useIsLoggedIn()
  const { isCurrentCartId } = useCurrentCart()
  const queryClient = useQueryClient()

  const overrides: RequestInit = {
    headers: { 'Content-Type': 'application/json' },
  }

  const getUserAppropriationsRequest: GetUserAppropriationsRequest = {
    ...cartBaseRequest,
    fields: RequestFieldsEnum.Default,
  }

  const getUserAppropriationsCacheKey = useOrgScopedQueryCacheKey(
    appropriationsClient.getUserAppropriations.name,
    getUserAppropriationsRequest
  )

  const getUserAppropriationsQuery = useQuery({
    queryKey: getUserAppropriationsCacheKey,
    queryFn: () => appropriationsClient.getUserAppropriations(getUserAppropriationsRequest, overrides),
    enabled: () => isLoggedIn && isCurrentCartId(cartBaseRequest.cartId),
  })

  const invalidate = async (options?: { skipRefetch?: boolean }) =>
    await queryClient.invalidateQueries({
      queryKey: getUserAppropriationsCacheKey,
      refetchType: options?.skipRefetch ? 'none' : 'active',
    })

  return {
    data: getUserAppropriationsQuery.data,
    isLoading: getUserAppropriationsQuery.isLoading,
    invalidate,
  }
}
